import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Trash2, Clock, Send, Loader2, CheckCircle2, AlertCircle, Film, RefreshCw, Link2, Pencil, Play, Save, Mic, HelpCircle, ChevronDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api, { apiError, fmtDateTime } from '../../lib/api';
import { refusFichier, urlBoutonPourCanal, boutonIncomplet as estBoutonIncomplet, lienDuCanal, canalAChoisir, lireDernierCanal, memoriserCanal, versChampDate, srcMedia, remplacerLigne,
  sondageVide, sondageDepuisPost, erreurSondage, corpsSondage, resumeSondage, refusAudio, dureeTexte } from '../../lib/canalPosts';
import CanalSondage from './CanalSondage';

// 17/09 : trois sortes de posts dans la même file — photo/vidéo (« media »), sondage (sendPoll), vocal (sendVoice, OGG/Opus).
const MODES = [['media', 'Photo / vidéo'], ['sondage', '📊 Sondage'], ['vocal', '🎤 Vocal']];
const TITRES = { media: ['Nouveau post', 'Modifier le post'], sondage: ['Nouveau sondage', 'Modifier le sondage'], vocal: ['Nouveau vocal', 'Modifier le vocal'] };
const ACCEPT_AUDIO = 'audio/*,.mp3,.m4a,.aac,.wav,.ogg,.oga,.opus,.flac,.webm,.amr,.3gp,.caf';

// Posts de canal avec bouton URL (13/09) : publiés par le BOT du canal (seul un bot peut mettre un bouton sous un post).
const STATUS = {
  pending: ['bg-amber-500/15 text-amber-300 border-amber-500/30', 'Programmé'],
  sending: ['bg-sky-500/15 text-sky-300 border-sky-500/30', 'Envoi…'],
  posted: ['bg-emerald-500/15 text-emerald-300 border-emerald-500/30', 'Publié'],
  failed: ['bg-rose-500/15 text-rose-300 border-rose-500/30', 'Échec'],
  canceled: ['bg-slate-500/15 text-slate-400 border-slate-500/30', 'Annulé'],
};
const LEGENDE_MAX = 1024;
const champ = 'block mt-1 w-full bg-[#18171C] border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none placeholder:text-slate-600';
const defaultWhen = () => {
  const d = new Date(Date.now() + 3600000);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
// 26/09 (#282) : borne basse du selecteur de date. Deux posts sont partis en pleine nuit
// parce que le mois saisi etait septembre au lieu d octobre — une date passee vaut
// « publie tout de suite ». Le navigateur refuse desormais la date sur place, et le
// serveur la refuse aussi (garde-fou des deux cotes).
const maintenantPourInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
const MSG_DATE_VIDE = 'Choisis la date et l’heure';
const msgDatePassee = (d) => `Le ${d.toLocaleString('fr-FR')} est déjà passé : ce post partirait tout de suite. Corrige la date, ou utilise « Publier maintenant ».`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

// 16/09 : vignette du média dans la file. Le fichier est supprimé du serveur après publication ou annulation
// (media_url vide) → icône seulement. Vidéo : tuile ▶, rien n'est chargé ni lu dans la liste.
// 17/09 : vignette légère (vignette_url, gardée après publication) en priorité ; sinon le fichier d'origine comme avant.
function Vignette({ r, onOuvrir }) {
  const [casse, setCasse] = useState(false);
  const leger = srcMedia(r.vignette_url, BACKEND);
  const src = srcMedia(r.media_url, BACKEND);
  if (r.type === 'sondage' || r.type === 'vocal') {
    return (
      <div data-testid={`canal-post-icone-${r.id}`} className="w-14 h-14 rounded-lg bg-black/40 border border-white/10 shrink-0 flex flex-col items-center justify-center text-slate-300">
        <span className="text-lg leading-none">{r.type === 'sondage' ? '📊' : '🎤'}</span>
        {r.type === 'vocal' && r.duree_s ? <span className="text-[10px] text-slate-400 mt-0.5 tabular-nums">{dureeTexte(r.duree_s)}</span> : null}
      </div>
    );
  }
  if ((!src && !leger) || casse) {
    return <div data-testid={`canal-post-icone-${r.id}`} className="w-14 h-14 rounded-lg bg-black/40 border border-white/10 shrink-0 flex items-center justify-center text-slate-400">{r.is_video ? <Film size={15} /> : '🖼️'}</div>;
  }
  const image = leger || (r.is_video ? '' : src);
  return (
    <button type="button" onClick={onOuvrir} title={r.is_video ? 'Voir la vidéo' : 'Voir la photo'} data-testid={`canal-post-vignette-${r.id}`}
      className="relative w-14 h-14 rounded-lg overflow-hidden bg-black/60 border border-white/10 shrink-0 hover:border-indigo-400/60">
      {image && <img src={image} alt="" loading="lazy" onError={() => setCasse(true)} className="w-full h-full object-cover" />}
      {r.is_video && (
        <span className={`absolute inset-0 flex items-center justify-center text-white ${image ? 'bg-black/25' : 'bg-gradient-to-br from-indigo-500/30 to-fuchsia-500/20'}`}><Play size={18} fill="currentColor" /></span>
      )}
    </button>
  );
}

// Aperçu grand format au clic sur une vignette (photo, ou vidéo avec ses commandes : lancée par l'utilisateur).

// 26/09 (Marlon) : « vaut mieux qu'on leur mette un petit guide, un petit tuto, pour bien
// integrer les boutons et les choses conseillees a faire — j'aurai la flemme si je dois
// m'en occuper pour tous les clients ». Visible par toute agence qui a le Canal.
function GuideCanal() {
  const [ouvert, setOuvert] = useState(false);
  const Etape = ({ n, titre, children }) => (
    <li className="flex gap-3">
      <span className="shrink-0 w-5 h-5 rounded-full bg-indigo-500/20 text-indigo-300 text-[11px] font-bold grid place-items-center mt-0.5">{n}</span>
      <div><span className="text-white font-medium">{titre}</span> <span className="text-slate-400">{children}</span></div>
    </li>
  );
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] mb-4" data-testid="canal-guide">
      <button type="button" onClick={() => setOuvert(!ouvert)} data-testid="canal-guide-ouvrir"
        className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm text-white">
          <HelpCircle size={15} className="text-indigo-300" /> Comment bien utiliser le Canal (boutons, sondages, conseils)
        </span>
        <ChevronDown size={15} className={`text-slate-500 transition-transform ${ouvert ? 'rotate-180' : ''}`} />
      </button>
      {ouvert && (
        <div className="px-4 pb-4 space-y-4 text-[13px] leading-relaxed">
          <div>
            <div className="text-white font-medium mb-1.5">Deux façons de publier</div>
            <p className="text-slate-400">
              <span className="text-white">Avec le compte du modèle</span> (recommandé) : rien à installer, les posts partent de son compte, déjà connecté ici.
              <br />
              <span className="text-white">Avec un bot</span> : nécessaire seulement si tu veux un <span className="text-white">bouton sous tes posts</span> ou des <span className="text-white">sondages</span> — Telegram réserve ces deux choses aux bots.
            </p>
          </div>
          <div>
            <div className="text-white font-medium mb-1.5">Créer le bot, une fois pour toutes (3 minutes)</div>
            <ol className="space-y-2">
              <Etape n="1" titre="Ouvre @BotFather sur Telegram">envoie <code className="text-indigo-300">/newbot</code>, donne un nom puis un identifiant qui finit par « bot ».</Etape>
              <Etape n="2" titre="Copie le token">BotFather te renvoie une ligne du type <code className="text-indigo-300">123456789:AA…</code> — c'est la clé, garde-la pour toi.</Etape>
              <Etape n="3" titre="Ajoute le bot à ton canal">Canal → Gérer → Administrateurs → Ajouter, cherche ton bot, et coche <span className="text-white">« Publier des messages »</span>.</Etape>
              <Etape n="4" titre="Reviens ici">« Connecter un canal » → « Avec un bot », choisis le canal, colle le token, Vérifier.</Etape>
            </ol>
          </div>
          <div>
            <div className="text-white font-medium mb-1.5">Les boutons</div>
            <p className="text-slate-400">
              Deux champs : le <span className="text-white">texte</span> (court : « Voir », « J'en profite », « Rejoindre ») et le <span className="text-white">lien</span>.
              Un bouton, c'est plus cliqué qu'un lien dans la légende, et ça évite l'aperçu de lien qui mange l'image.
              Un seul bouton par post : deux choix, c'est un choix de moins fait.
            </p>
          </div>
          <div>
            <div className="text-white font-medium mb-1.5">Ce qui marche</div>
            <ul className="text-slate-400 space-y-1 list-disc pl-4">
              <li>Programme à l'avance : prépare la semaine en une fois plutôt que de publier au fil de l'eau.</li>
              <li>Une vidéo verticale retient plus qu'une photo ; garde-la courte.</li>
              <li>Légende brève, une idée par post, et l'appel à l'action dans le bouton — pas dans le texte.</li>
              <li>Un sondage de temps en temps : c'est ce qui fait réagir un canal silencieux (mode bot).</li>
              <li>Évite les publications en rafale : espace-les, sinon les abonnés coupent les notifications.</li>
            </ul>
          </div>
          <div className="text-slate-500 text-[12px] border-t border-white/5 pt-3">
            Un post programmé part tout seul à l'heure prévue, même si personne n'est connecté.
            S'il échoue, la raison s'affiche dans la file des posts.
          </div>
        </div>
      )}
    </div>
  );
}

function ApercuGrand({ post, onFermer }) {
  const [casse, setCasse] = useState(false);
  useEffect(() => { setCasse(false); }, [post]);
  const src = post ? srcMedia(post.media_url, BACKEND) : '';
  const leger = post ? srcMedia(post.vignette_url, BACKEND) : '';   // 17/09 : fichier d'origine supprimé → la vignette
  return (
    <Dialog open={!!post} onOpenChange={(o) => { if (!o) onFermer(); }}>
      <DialogContent aria-describedby={undefined} data-testid="canal-post-grand" className="max-w-3xl bg-[#111015] border-white/10 p-4 text-white">
        <DialogTitle className="text-sm font-semibold pr-6 truncate">{post?.legende || (post?.is_video ? 'Vidéo du post' : 'Photo du post')}</DialogTitle>
        {post && src && !casse && (post.is_video
          ? <video key={src} src={src} controls playsInline preload="metadata" onError={() => setCasse(true)} data-testid="canal-post-grand-video" className="w-full max-h-[70vh] rounded-lg bg-black" />
          : <img src={src} alt="" onError={() => setCasse(true)} data-testid="canal-post-grand-photo" className="w-full max-h-[70vh] object-contain rounded-lg bg-black/40" />)}
        {post && (!src || casse) && leger && (
          <div className="space-y-2">
            <img src={leger} alt="" data-testid="canal-post-grand-vignette" className="w-full max-h-[60vh] object-contain rounded-lg bg-black/40" />
            <div className="text-[11px] text-slate-500 text-center">Aperçu réduit : le fichier d'origine n'est plus sur le serveur (supprimé après publication).</div>
          </div>
        )}
        {post && (!src || casse) && !leger && <div className="py-10 text-center text-sm text-slate-400">Le fichier n'est plus disponible sur le serveur.</div>}
        {post && <div className="text-[11px] text-slate-500">📢 {post.chat_title} · {fmtDateTime(post.scheduled_at)}{post.bouton_texte ? ` · 🔘 ${post.bouton_texte}` : ''}</div>}
      </DialogContent>
    </Dialog>
  );
}

function ConnecterCanal({ onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [token, setToken] = useState('');
  const [canal, setCanal] = useState('');
  const [enCours, setEnCours] = useState(false);
  // 13/09 : les canaux (même privés) sont déjà connus via le compte de la modèle, comme pour les stories → on les propose.
  const [comptes, setComptes] = useState([]);
  const [compte, setCompte] = useState('');
  const [canaux, setCanaux] = useState([]);
  const [autre, setAutre] = useState(false);
  useEffect(() => {
    if (!ouvert) return;
    api.get('/accounts').then(({ data }) => {
      const prets = (data || []).filter(a => a.status === 'ready');
      setComptes(prets);
      setCompte(prev => prev || prets[0]?.id || '');
    }).catch(() => {});
  }, [ouvert]);
  useEffect(() => {
    if (!ouvert || !compte) return;
    api.get('/tracking/channels', { params: { account_id: compte } }).then(({ data }) => {
      const liste = Array.isArray(data) ? data : (data.channels || []);
      setCanaux(liste);
      setCanal(prev => (liste.some(c => c.id === prev) ? prev : (liste[0]?.id || '')));
    }).catch(() => setCanaux([]));
  }, [ouvert, compte]);
  // 26/09 (Marlon) : par defaut on publie AVEC LE COMPTE deja connecte — plus de bot a
  // creer chez BotFather, plus de token. Le bot reste disponible pour les boutons sous
  // les posts et les sondages, que Telegram reserve aux bots.
  const [mode, setMode] = useState('compte');
  const valider = async () => {
    setEnCours(true);
    if (mode === 'compte') {
      try {
        const titre = autre ? '' : (canaux.find(c => c.id === canal)?.title || '');
        const { data } = await api.post('/canal-comptes', { account_id: compte, chat_id: canal, titre: titre || undefined });
        toast.success(`Canal « ${data.chat_title} » connecté — rien d'autre à faire`);
        setCanal(''); setOuvert(false); onFait(data.id);
      } catch (e) { toast.error(apiError(e, 'Connexion impossible')); }
      finally { setEnCours(false); }
      return;
    }
    try {
      // 15/09 (#141) : le canal est gardé dès que le token est bon, même si le bot ne peut pas encore publier.
      const titre = autre ? '' : (canaux.find(c => c.id === canal)?.title || '');
      const { data } = await api.post('/canal-bots', { token: token.trim(), canal: canal.trim(), account_id: compte || undefined, canal_titre: titre || undefined });
      toast[data.peut_poster ? 'success' : 'warning'](data.peut_poster ? `Canal « ${data.chat_title} » connecté` : `Canal enregistré. ${data.erreur}`);
      setToken(''); setCanal(''); setOuvert(false); onFait(data.id);
    } catch (e) { toast.error(apiError(e, 'Connexion impossible')); }
    finally { setEnCours(false); }
  };
  if (!ouvert) {
    return <button data-testid="canal-connecter" onClick={() => setOuvert(true)} className="text-xs text-indigo-300 hover:text-indigo-200 flex items-center gap-1"><Plus size={13} /> Connecter un canal</button>;
  }
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-3" data-testid="canal-connexion">
      <div className="flex gap-2" data-testid="canal-mode">
        {[['compte', 'Avec le compte du modèle', 'Rien à installer'], ['bot', 'Avec un bot', 'Pour les boutons et sondages']].map(([k, titre, sous]) => (
          <button key={k} type="button" onClick={() => setMode(k)}
            data-testid={`canal-mode-${k}`}
            className={`flex-1 text-left rounded-xl border px-3 py-2 transition-colors ${mode === k
              ? 'border-indigo-400/60 bg-indigo-500/10' : 'border-white/10 bg-white/[0.02] hover:bg-white/[0.04]'}`}>
            <div className="text-[12px] font-medium text-white">{titre}</div>
            <div className="text-[11px] text-slate-500">{sous}</div>
          </button>
        ))}
      </div>
      <div className="text-[12px] text-slate-400 leading-relaxed">
        {mode === 'compte' ? (
          <>Choisis le modèle et son canal : les posts partiront <span className="text-white">depuis son compte</span>, celui qui est déjà connecté ici.
            Rien à créer, aucun token. <span className="text-slate-500">Un bouton sous le post ou un sondage demande un bot (Telegram les réserve aux bots).</span></>
        ) : (
          <>1. Ajoute ton bot en <span className="text-white">administrateur du canal</span> avec le droit « Publier des messages » (Telegram → canal → Administrateurs → chercher le bot).<br />
            2. Choisis le canal ci-dessous et colle le <span className="text-white">token du bot</span> (@BotFather → /mybots → API Token).</>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block text-[11px] uppercase tracking-wider text-slate-500">Modèle
          <select data-testid="canal-compte" value={compte} onChange={e => setCompte(e.target.value)} className={champ}>
            {comptes.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>
        {!autre ? (
          <label className="block text-[11px] uppercase tracking-wider text-slate-500">Canal
            <select data-testid="canal-choix" value={canal} onChange={e => setCanal(e.target.value)} className={champ}>
              {canaux.length === 0 && <option value="">Aucun canal (le compte doit en être admin)</option>}
              {canaux.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
          </label>
        ) : (
          <label className="block text-[11px] uppercase tracking-wider text-slate-500">Canal public
            <input data-testid="canal-identifiant" value={canal} onChange={e => setCanal(e.target.value)} placeholder="@moncanal ou https://t.me/moncanal" className={champ} />
          </label>
        )}
      </div>
      <button type="button" onClick={() => { setAutre(!autre); setCanal(''); }} className="text-[11px] text-slate-500 hover:text-white">
        {autre ? '← Choisir dans mes canaux' : 'Le canal n\'est pas dans la liste ? Saisir un @nom public'}
      </button>
      {mode === 'bot' && (
        <label className="block text-[11px] uppercase tracking-wider text-slate-500">Token du bot
          <input data-testid="canal-token" value={token} onChange={e => setToken(e.target.value)} placeholder="123456789:AA…" className={champ} autoComplete="off" />
        </label>
      )}
      <div className="flex gap-2 justify-end">
        <button onClick={() => setOuvert(false)} className="px-3 py-2 text-xs text-slate-400 hover:text-white">Annuler</button>
        <Button data-testid="canal-verifier" onClick={valider}
          disabled={enCours || !canal.trim() || (mode === 'bot' && !token.trim())}
          className="bg-indigo-500 hover:bg-indigo-600 h-8 text-xs">
          {enCours ? <Loader2 size={13} className="mr-1.5 animate-spin" /> : null}
          {mode === 'compte' ? 'Connecter ce canal' : 'Vérifier et enregistrer'}
        </Button>
      </div>
    </div>
  );
}

export default function CanalPosts() {
  const [bots, setBots] = useState([]);
  const [botId, setBotId] = useState('');
  const [rows, setRows] = useState([]);
  const [file, setFile] = useState(null);
  const [apercu, setApercu] = useState('');
  const [legende, setLegende] = useState('');
  const [boutonTexte, setBoutonTexte] = useState('');
  const [boutonUrl, setBoutonUrl] = useState('');
  const [when, setWhen] = useState(defaultWhen());
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [modeles, setModeles] = useState([]);
  const [liens, setLiens] = useState({});
  const [survol, setSurvol] = useState(false);
  const survolCpt = useRef(0);
  const preRempli = useRef('');
  const urlCourante = useRef('');
  urlCourante.current = boutonUrl;
  const [edition, setEdition] = useState(null);   // 16/09 : post programmé en cours de modification
  const [grand, setGrand] = useState(null);       // post affiché en grand
  const formRef = useRef(null);
  const [mode, setMode] = useState('media');      // 17/09 : 'media' | 'sondage' | 'vocal'
  const [sondage, setSondage] = useState(sondageVide());
  const [dureeLue, setDureeLue] = useState(0);    // vocal : durée lue par le lecteur (réécoute avant de programmer)

  // Liste rechargée à l'ouverture de la page et après chaque connexion ; le dernier canal choisi reste sélectionné.
  const chargerBots = useCallback((nouveau) => {
    api.get('/canal-bots').then(({ data }) => {
      const liste = Array.isArray(data) ? data : [];
      setBots(liste);
      setBotId(prev => canalAChoisir(liste, typeof nouveau === 'string' ? nouveau : prev, lireDernierCanal()));
    }).catch(() => {});
  }, []);
  useEffect(() => { chargerBots(); }, [chargerBots]);
  useEffect(() => { memoriserCanal(botId); }, [botId]);
  useEffect(() => {
    api.get('/canal-modeles').then(({ data }) => setModeles(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);
  const charger = useCallback(() => api.get('/canal-posts', { params: botId ? { bot_id: botId } : {} })
    .then(({ data }) => { const liste = data || []; setRows(liste); return liste; }).catch(() => null), [botId]);
  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { const t = setInterval(charger, 20000); return () => clearInterval(t); }, [charger]);

  const choisir = (f) => {
    setFile(f || null);
    if (apercu) URL.revokeObjectURL(apercu);
    setApercu(f ? URL.createObjectURL(f) : '');
  };
  const bot = bots.find(b => b.id === botId);
  const video = file && file.type.startsWith('video');
  const botPost = edition ? bots.find(b => b.id === edition.bot_id) : bot;
  const ligneEditee = edition ? rows.find(x => x.id === edition.id) : null;
  const partie = Boolean(ligneEditee && ligneEditee.status !== 'pending');   // publié / en cours d'envoi pendant la modification
  const mediaActuel = !file && edition ? srcMedia(edition.media_url, BACKEND) : '';
  const apercuSrc = file ? apercu : mediaActuel;
  const apercuVideo = file ? Boolean(video) : Boolean(mediaActuel && edition.is_video);
  const lienCanal = lienDuCanal(bot, modeles);
  const boutonIncomplet = estBoutonIncomplet(boutonTexte, boutonUrl, lienCanal);
  const modeleDe = (b) => modeles.find(m => m.id === b?.account_id);

  // URL du bouton pré-remplie avec le lien de la modèle du canal (jamais par-dessus une URL tapée à la main).
  useEffect(() => {
    const r = urlBoutonPourCanal(urlCourante.current, preRempli.current, lienCanal);
    preRempli.current = r.pre;
    if (r.url !== urlCourante.current) setBoutonUrl(r.url);
  }, [botId, lienCanal]);

  const prendre = (f, plusieurs) => {
    if (!f) return;
    if (mode === 'sondage') { toast.error('Un sondage n\'a pas de fichier : choisis « Photo / vidéo » ou « Vocal »'); return; }
    const refus = mode === 'vocal' ? refusAudio(f) : refusFichier(f);
    if (refus) { toast.error(refus); return; }
    if (plusieurs) toast(mode === 'vocal' ? 'Un seul audio par vocal : le premier est gardé' : 'Un seul média par post : le premier est gardé');
    setDureeLue(0);
    choisir(f);
  };
  const changerMode = (m) => {
    if (edition || m === mode) return;   // en modification, la sorte de post ne change pas
    choisir(null);
    if (fileRef.current) fileRef.current.value = '';
    setDureeLue(0);
    setMode(m);
  };
  const erreurDuSondage = mode === 'sondage' ? erreurSondage(sondage) : '';
  // Vocal : réécoute du fichier choisi (lien local) ou, en modification, de l'OGG converti par le serveur.
  const srcVocal = mode !== 'vocal' ? '' : (file ? apercu : (edition ? srcMedia(edition.media_url, BACKEND) : ''));
  const dureeVocal = file ? dureeLue : ((edition && edition.duree_s) || dureeLue);
  const bloque = saving || (mode === 'sondage' ? Boolean(erreurDuSondage) : legende.length > LEGENDE_MAX);
  const avecFichiers = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
  const glisser = {
    onDragEnter: (e) => { if (!avecFichiers(e)) return; e.preventDefault(); survolCpt.current += 1; setSurvol(true); },
    onDragOver: (e) => { if (!avecFichiers(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; },
    onDragLeave: (e) => { if (!avecFichiers(e)) return; survolCpt.current = Math.max(0, survolCpt.current - 1); if (!survolCpt.current) setSurvol(false); },
    onDrop: (e) => {
      if (!avecFichiers(e)) return;
      e.preventDefault(); survolCpt.current = 0; setSurvol(false);
      const fichiers = e.dataTransfer.files || [];
      prendre(fichiers[0], fichiers.length > 1);
    },
  };

  const envoyer = async (maintenant) => {
    // 26/09 (#282) : une date passee = publication immediate. On arrete avant l envoi.
    if (!maintenant) {
      const quandD = new Date(when);
      if (!when || Number.isNaN(quandD.getTime())) { toast.error(MSG_DATE_VIDE); return; }
      if (quandD.getTime() < Date.now() - 15 * 60000) { toast.error(msgDatePassee(quandD)); return; }
    }
    if (!bot) { toast.error('Connecte d\'abord un canal'); return; }
    if (!bot.peut_poster) { toast.error('Le bot ne peut pas encore publier sur ce canal : « Revérifier » d\'abord'); return; }
    if (mode === 'sondage' && erreurDuSondage) { toast.error(erreurDuSondage); return; }
    if (mode !== 'sondage' && !file) { toast.error(mode === 'vocal' ? 'Ajoute un fichier audio' : 'Ajoute une photo ou une vidéo'); return; }
    if (boutonIncomplet) { toast.error('Remplis le texte ET l\'URL du bouton'); return; }
    setSaving(true);
    const nom = { media: ['Post', 'publié'], sondage: ['Sondage', 'publié'], vocal: ['Vocal', 'publié'] }[mode];
    try {
      const quand = maintenant ? new Date().toISOString() : new Date(when).toISOString();
      if (mode === 'sondage') {
        await api.post('/canal-posts/sondage', corpsSondage(sondage, { bot_id: bot.id, bouton_texte: boutonTexte, bouton_url: boutonTexte.trim() ? boutonUrl : '', scheduled_at: quand }));
      } else {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('bot_id', bot.id);
        fd.append('legende', legende);
        fd.append('bouton_texte', boutonTexte);
        fd.append('bouton_url', boutonTexte.trim() ? boutonUrl : '');   // sans texte = sans bouton (URL pré-remplie ignorée)
        fd.append('scheduled_at', quand);
        if (mode === 'vocal') fd.append('type_post', 'vocal');   // converti en OGG/Opus par le serveur
        await api.post('/canal-posts/schedule', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      }
      toast.success(maintenant ? `${nom[0]} envoyé — ${nom[1]} dans ~30 s` : `${nom[0]} programmé`);
      choisir(null); setLegende(''); setBoutonTexte(''); setSondage(sondageVide()); setDureeLue(0);
      if (boutonUrl === preRempli.current) { setBoutonUrl(lienCanal); preRempli.current = lienCanal; }  // une URL tapée à la main reste
      if (fileRef.current) fileRef.current.value = '';
      charger();
    } catch (e) { toast.error(apiError(e, 'Échec de la programmation')); }
    finally { setSaving(false); }
  };
  // 16/09 : « Modifier » un post programmé — le formulaire se pré-remplit, « Enregistrer » met la ligne à jour sur place.
  const quitterEdition = () => {
    setEdition(null);
    choisir(null);
    if (fileRef.current) fileRef.current.value = '';
    setLegende(''); setBoutonTexte(''); setSondage(sondageVide()); setDureeLue(0);
    setBoutonUrl(lienCanal); preRempli.current = lienCanal;
    setWhen(defaultWhen());
  };
  const modifier = (r) => {
    if (r.bot_id && r.bot_id !== botId && bots.some(b => b.id === r.bot_id)) setBotId(r.bot_id);
    choisir(null);
    if (fileRef.current) fileRef.current.value = '';
    setEdition(r);
    setMode(r.type || 'media');   // 17/09 : le formulaire prend la sorte du post (photo/vidéo, sondage, vocal)
    setSondage(r.type === 'sondage' ? sondageDepuisPost(r) : sondageVide());
    setDureeLue(0);
    setLegende(r.legende || ''); setBoutonTexte(r.bouton_texte || '');
    // URL du post EXACTEMENT comme enregistrée ; sans bouton, le lien de la modèle est pré-rempli comme à la création.
    const url = r.bouton_url || lienDuCanal(bots.find(b => b.id === r.bot_id), modeles);
    preRempli.current = r.bouton_url ? '' : url;
    setBoutonUrl(url);
    setWhen(versChampDate(r.scheduled_at) || defaultWhen());
    if (formRef.current && formRef.current.scrollIntoView) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const choisirCanal = (id) => {
    if (edition && id !== edition.bot_id) quitterEdition();
    setBotId(id);
  };
  const enregistrer = async () => {
    if (!edition) return;
    const date = new Date(when);
    if (!when || Number.isNaN(date.getTime())) { toast.error('Choisis la date et l\'heure'); return; }
    if (boutonIncomplet) { toast.error('Remplis le texte ET l\'URL du bouton'); return; }
    if (mode === 'sondage' && erreurDuSondage) { toast.error(erreurDuSondage); return; }
    setSaving(true);
    try {
      let data;
      if (mode === 'sondage') {
        ({ data } = await api.patch(`/canal-posts/${edition.id}/sondage`, corpsSondage(sondage, { bouton_texte: boutonTexte, bouton_url: boutonTexte.trim() ? boutonUrl : '', scheduled_at: date.toISOString() })));
      } else {
        const fd = new FormData();
        if (file) fd.append('file', file);   // facultatif : sans fichier, la photo/vidéo (ou l'audio) actuelle est gardée
        fd.append('legende', legende);
        fd.append('bouton_texte', boutonTexte);
        fd.append('bouton_url', boutonTexte.trim() ? boutonUrl : '');
        fd.append('scheduled_at', date.toISOString());
        ({ data } = await api.patch(`/canal-posts/${edition.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }));
      }
      setRows(prev => remplacerLigne(prev, data));
      toast.success({ media: 'Post modifié', sondage: 'Sondage modifié', vocal: 'Vocal modifié' }[mode] || 'Post modifié');
      quitterEdition();
    } catch (e) {
      const code = e?.response?.status;
      toast.error(apiError(e, 'Modification impossible'));
      if (code === 409 || code === 404) {
        const liste = await charger();   // statut à jour : bandeau « plus programmé » dans le formulaire
        if (code === 404 || (liste && !liste.some(x => x.id === edition.id))) quitterEdition();
      }
    } finally { setSaving(false); }
  };
  const annuler = async (id) => {
    try {
      await api.delete(`/canal-posts/${id}`);
      setRows(prev => prev.map(r => (r.id === id ? { ...r, status: 'canceled' } : r)));
      if (edition && edition.id === id) quitterEdition();
    }
    catch (e) { toast.error(apiError(e, 'Échec')); }
  };
  const maintenant = async (id) => {
    try { await api.post(`/canal-posts/${id}/post-now`); toast.success('Le post part dans ~30 s'); charger(); }
    catch (e) { toast.error(apiError(e, 'Échec')); }
  };
  const reverifier = async (id) => {
    try { const { data } = await api.post(`/canal-bots/${id}/verifier`); toast[data.peut_poster ? 'success' : 'warning'](data.peut_poster ? 'Le bot peut publier' : data.erreur); chargerBots(); }
    catch (e) { toast.error(apiError(e, 'Vérification impossible')); }
  };
  const rattacher = async (id, accountId) => {
    try {
      await api.patch(`/canal-bots/${id}`, { account_id: accountId || null });
      setBots(prev => prev.map(b => (b.id === id ? { ...b, account_id: accountId || null } : b)));
    } catch (e) { toast.error(apiError(e, 'Échec')); }
  };
  const enregistrerLien = async (id, saisie) => {
    try {
      const { data } = await api.put(`/canal-modeles/${id}/lien`, { lien_telegram: saisie.trim() || null });
      setModeles(prev => prev.map(m => (m.id === id ? { ...m, lien_telegram: data.lien_telegram } : m)));
      setLiens(prev => { const n = { ...prev }; delete n[id]; return n; });
      toast.success('Lien enregistré');
    } catch (e) { toast.error(apiError(e, 'Lien refusé')); }
  };
  const retirer = async (id) => {
    if (!window.confirm('Retirer ce canal de ChatHub ? Les posts programmés dessus seront annulés.')) return;
    try { await api.delete(`/canal-bots/${id}`); chargerBots(); charger(); }
    catch (e) { toast.error(apiError(e, 'Échec')); }
  };

  return (
    <div className="space-y-4" data-testid="canal-posts">
      <GuideCanal />
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-white">Canaux connectés</div>
          <ConnecterCanal onFait={chargerBots} />
        </div>
        {bots.length === 0 && <div className="text-[12px] text-slate-500">Aucun canal : connectes-en un — avec le compte du modèle, il n'y a rien à installer.</div>}
        <div className="flex flex-wrap gap-2">
          {bots.map(b => (
            <div key={b.id} data-testid={`canal-bot-${b.id}`} onClick={() => choisirCanal(b.id)}
              className={`cursor-pointer rounded-lg border px-3 py-2 text-[12px] flex items-center gap-2 ${b.id === botId ? 'border-indigo-400/60 bg-indigo-500/10' : 'border-white/10 bg-white/[0.03]'}`}>
              {b.peut_poster ? <CheckCircle2 size={13} className="text-emerald-400" /> : <AlertCircle size={13} className="text-amber-400" />}
              <span className="text-white">📢 {b.chat_title}</span>
              <span className="text-slate-500">@{b.bot_username}{modeleDe(b) ? ` · ${modeleDe(b).name}` : ''}</span>
              <button onClick={(e) => { e.stopPropagation(); reverifier(b.id); }} title="Revérifier les droits du bot" className="text-slate-500 hover:text-white"><RefreshCw size={12} /></button>
              <button onClick={(e) => { e.stopPropagation(); retirer(b.id); }} title="Retirer" className="text-slate-500 hover:text-rose-400"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
        {bot && !bot.peut_poster && (
          <div className="text-[11px] text-amber-300 flex flex-wrap items-center gap-2" data-testid="canal-bot-erreur">
            <span>{bot.erreur}</span>
            <button data-testid="canal-bot-reverifier" onClick={() => reverifier(bot.id)} className="underline text-amber-200 hover:text-white">Revérifier</button>
          </div>
        )}
        {bot && (
          <label className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">Modèle du canal
            <select data-testid="canal-bot-modele" value={bot.account_id || ''} onChange={e => rattacher(bot.id, e.target.value)} className="bg-[#18171C] border border-white/10 rounded-lg px-2 py-1 text-[12px] text-white outline-none">
              <option value="">— à choisir —</option>
              {modeles.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
            {!bot.account_id && <span className="text-amber-300">Choisis-la pour pré-remplir le bouton</span>}
          </label>
        )}
        {modeles.length > 0 && (
          <div className="pt-2 border-t border-white/5 space-y-1.5" data-testid="canal-liens-modeles">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Lien du bouton par défaut</div>
            {modeles.map(m => {
              const saisie = liens[m.id] ?? (m.lien_telegram || '');
              const change = saisie.trim() !== (m.lien_telegram || '');
              return (
                <div key={m.id} className="flex items-center gap-2">
                  <span className="text-[12px] text-white w-24 shrink-0 truncate">{m.name}</span>
                  <input data-testid={`canal-lien-${m.id}`} value={saisie} onChange={e => { const val = e.target.value; setLiens(p => ({ ...p, [m.id]: val })); }} placeholder="https://t.me/…"
                    className="flex-1 min-w-0 bg-[#18171C] border border-white/10 rounded-lg px-2 py-1 text-[12px] text-white outline-none placeholder:text-slate-600" />
                  {change && <button data-testid={`canal-lien-ok-${m.id}`} onClick={() => enregistrerLien(m.id, saisie)} className="shrink-0 text-[11px] px-2 py-1 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white">Enregistrer</button>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div {...glisser} ref={formRef} data-testid="canal-post-form" data-survol={survol ? '1' : '0'} data-edition={edition ? edition.id : ''} className={`rounded-2xl border bg-white/[0.02] p-5 space-y-4 transition-colors scroll-mt-4 ${survol ? 'border-indigo-400/60 ring-2 ring-indigo-400/30' : (edition ? 'border-indigo-400/40' : 'border-white/10')}`}>
          <div className="text-sm font-semibold text-white">{(TITRES[mode] || TITRES.media)[edition ? 1 : 0]}{botPost ? ` — ${botPost.chat_title}` : ''}</div>
          <div className="flex flex-wrap gap-1 p-1 rounded-lg bg-white/5 border border-white/10 w-fit" data-testid="canal-post-modes">
            {MODES.map(([v, l]) => (
              <button key={v} type="button" onClick={() => changerMode(v)} data-testid={`canal-post-mode-${v}`} aria-pressed={mode === v} disabled={Boolean(edition) && mode !== v}
                className={`px-3 h-7 rounded-md text-xs font-medium transition-colors disabled:opacity-40 ${mode === v ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}>{l}</button>
            ))}
          </div>
          {edition && (
            <div data-testid="canal-post-edition" className="text-[11px] text-slate-400 -mt-2">
              Programmé pour le {fmtDateTime(edition.scheduled_at)} — change ce que tu veux puis «&nbsp;Enregistrer&nbsp;».{mode === 'media' ? ' Sans nouveau fichier, la photo/vidéo actuelle est gardée.' : mode === 'vocal' ? ' Sans nouveau fichier, l\'audio actuel est gardé.' : ''}
            </div>
          )}
          {edition && partie && (
            <div data-testid="canal-post-parti" className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">
              Ce post n'est plus programmé ({(STATUS[ligneEditee.status] || [])[1] || ligneEditee.status}) : la modification ne peut plus être enregistrée.
            </div>
          )}
          {mode === 'sondage' && <CanalSondage value={sondage} onChange={setSondage} />}
          {mode === 'sondage' && erreurDuSondage && (sondage.question.trim() || sondage.options.some(o => o.trim())) && (
            <div data-testid="sondage-erreur" className="text-[11px] text-amber-300 -mt-1">{erreurDuSondage}</div>
          )}
          {mode === 'vocal' && (
            <div data-testid="canal-vocal-zone" onClick={() => fileRef.current?.click()} className={`cursor-pointer rounded-xl border border-dashed transition-colors min-h-[7rem] flex items-center justify-center px-4 py-4 ${survol ? 'border-indigo-400 bg-indigo-500/10' : 'border-white/15 bg-white/[0.02] hover:border-indigo-500/40'}`}>
              {survol && <div className="text-center text-indigo-200 text-sm"><Plus className="mx-auto mb-1" size={22} /> Dépose ici</div>}
              {!survol && srcVocal && (
                <div className="w-full space-y-2 text-center" onClick={e => e.stopPropagation()}>
                  <div className="text-sm text-white flex items-center justify-center gap-1.5 min-w-0" data-testid="canal-vocal-nom">
                    <Mic size={15} className="shrink-0 text-indigo-300" /><span className="truncate">{file ? file.name : 'Vocal actuel'}</span>
                    {dureeVocal ? <span className="text-slate-400 tabular-nums shrink-0" data-testid="canal-vocal-duree">· {dureeTexte(dureeVocal)}</span> : null}
                  </div>
                  <audio key={srcVocal} src={srcVocal} controls preload="metadata" data-testid="canal-vocal-lecteur" className="w-full h-10"
                    onLoadedMetadata={e => { const d = e.currentTarget.duration; if (Number.isFinite(d) && d > 0) setDureeLue(d); }} />
                  <button type="button" onClick={() => fileRef.current?.click()} className="text-[11px] text-slate-400 underline hover:text-white">{edition ? 'Remplacer l\'audio' : 'Choisir un autre fichier'}</button>
                </div>
              )}
              {!survol && !srcVocal && (
                <div className="text-center text-slate-500 text-sm"><Mic className="mx-auto mb-1" size={22} /> Ajouter ou glisser un fichier audio
                  <div className="text-[11px] mt-1">MP3, M4A, WAV, OGG… · 50 Mo max · converti en vocal Telegram</div></div>
              )}
            </div>
          )}
          {mode === 'vocal' && edition && file && (
            <div data-testid="canal-vocal-remplacement" className="text-[11px] text-indigo-200 -mt-2 flex flex-wrap items-center gap-2">
              Nouvel audio : il remplacera l'actuel à l'enregistrement.
              <button type="button" onClick={() => { choisir(null); setDureeLue(0); if (fileRef.current) fileRef.current.value = ''; }} className="underline text-slate-400 hover:text-white">Garder l'actuel</button>
            </div>
          )}
          {mode === 'media' && (<>
          <div data-testid="canal-post-zone" onClick={() => fileRef.current?.click()} className={`cursor-pointer rounded-xl border border-dashed transition-colors h-44 flex items-center justify-center overflow-hidden ${survol ? 'border-indigo-400 bg-indigo-500/10' : 'border-white/15 bg-white/[0.02] hover:border-indigo-500/40'}`}>
            {survol && <div className="text-center text-indigo-200 text-sm"><Plus className="mx-auto mb-1" size={22} /> Dépose ici</div>}
            {!survol && !apercuSrc && (edition
              ? <div className="text-center text-amber-300 text-sm px-4"><Plus className="mx-auto mb-1" size={22} /> Fichier absent du serveur : ajoute ou glisse une photo / vidéo<div className="text-[11px] mt-1 text-slate-500">photo 10 Mo · vidéo 50 Mo max</div></div>
              : <div className="text-center text-slate-500 text-sm"><Plus className="mx-auto mb-1" size={22} /> Ajouter ou glisser une photo / vidéo<div className="text-[11px] mt-1">photo 10 Mo · vidéo 50 Mo max</div></div>)}
            {!survol && apercuSrc && !apercuVideo && <img src={apercuSrc} alt="" className="h-full object-contain" />}
            {!survol && file && video && <video src={apercu} className="h-full object-contain" muted controls />}
            {!survol && !file && apercuVideo && (
              <div className="text-center text-slate-300 text-sm" data-testid="canal-post-video-actuelle">
                <Play className="mx-auto mb-1" size={22} /> Vidéo actuelle
                <div className="text-[11px] mt-1 text-slate-500">clique pour la remplacer · <button type="button" onClick={(e) => { e.stopPropagation(); setGrand(edition); }} className="underline hover:text-white">la voir</button></div>
              </div>
            )}
          </div>
          {edition && (file ? (
            <div data-testid="canal-post-remplacement" className="text-[11px] text-indigo-200 -mt-2 flex flex-wrap items-center gap-2">
              Nouvelle {video ? 'vidéo' : 'photo'} : elle remplacera l'actuelle à l'enregistrement.
              <button type="button" onClick={() => { choisir(null); if (fileRef.current) fileRef.current.value = ''; }} className="underline text-slate-400 hover:text-white">Garder l'actuelle</button>
            </div>
          ) : (
            <div className="text-[11px] text-slate-500 -mt-2">Pour changer la photo/vidéo : clique sur la zone ou glisse un fichier (facultatif).</div>
          ))}
          </>)}
          <input ref={fileRef} type="file" accept={mode === 'vocal' ? ACCEPT_AUDIO : 'image/jpeg,image/png,image/webp,video/mp4,video/quicktime,video/x-m4v,.m4v'} className="hidden" onChange={e => prendre(e.target.files?.[0])} data-testid="canal-post-file" />
          {mode !== 'sondage' && (
          <label className="block text-[11px] uppercase tracking-wider text-slate-500">Légende
            <textarea data-testid="canal-post-legende" value={legende} onChange={e => setLegende(e.target.value)} rows={mode === 'vocal' ? 2 : 4} placeholder={mode === 'vocal' ? 'Texte sous le vocal (facultatif)…' : 'Texte du post…'} className={`${champ} resize-none`} />
            <span className={`block text-right normal-case tracking-normal text-[10px] ${legende.length > LEGENDE_MAX ? 'text-rose-400' : 'text-slate-600'}`}>{legende.length} / {LEGENDE_MAX}</span>
          </label>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block text-[11px] uppercase tracking-wider text-slate-500">Texte du bouton
              <input data-testid="canal-post-bouton-texte" value={boutonTexte} onChange={e => setBoutonTexte(e.target.value)} placeholder="💊 PRENDS RDV" className={champ} />
            </label>
            <label className="block text-[11px] uppercase tracking-wider text-slate-500">URL du bouton
              <input data-testid="canal-post-bouton-url" value={boutonUrl} onChange={e => setBoutonUrl(e.target.value)} placeholder="https://…" className={champ} />
            </label>
          </div>
          {boutonIncomplet && <div className="text-[11px] text-amber-300">Remplis le texte ET l'URL du bouton (ou aucun des deux).</div>}
          <label className="block text-[11px] uppercase tracking-wider text-slate-500">Date & heure
            <input type="datetime-local" min={maintenantPourInput()} value={when} onChange={e => setWhen(e.target.value)} data-testid="canal-post-when" className={champ} />
          </label>
          {/* 17/09 : flex-wrap — sur téléphone « Publier maintenant » (texte sur une ligne) élargissait la carte au-delà de l'écran */}
          <div className="flex flex-wrap gap-2 pt-1">
            {edition ? (
              <>
                <Button data-testid="canal-post-enregistrer" onClick={enregistrer} disabled={bloque || partie || !botPost || !botPost.peut_poster} className="flex-1 bg-indigo-500 hover:bg-indigo-600">
                  {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Save size={15} className="mr-1.5" />} Enregistrer
                </Button>
                <Button variant="outline" data-testid="canal-post-annuler-modif" onClick={quitterEdition} disabled={saving} className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10">
                  Annuler la modification
                </Button>
              </>
            ) : (
              <>
                <Button data-testid="canal-post-programmer" onClick={() => envoyer(false)} disabled={bloque || !bot || !bot.peut_poster} className="flex-1 bg-indigo-500 hover:bg-indigo-600">
                  {saving ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Clock size={15} className="mr-1.5" />} Programmer
                </Button>
                <Button variant="outline" data-testid="canal-post-maintenant" onClick={() => envoyer(true)} disabled={bloque || !bot || !bot.peut_poster} className="border-white/10 bg-white/5 text-slate-200 hover:bg-white/10">
                  <Send size={14} className="mr-1.5" /> Publier maintenant
                </Button>
              </>
            )}
          </div>
          {mode === 'sondage' && (sondage.question.trim() || boutonTexte.trim()) && (
            <div className="rounded-xl bg-[#0E1621] border border-white/10 p-2 max-w-[320px]" data-testid="canal-post-apercu">
              <div className="text-[10px] text-slate-500 px-1 pb-1">Aperçu dans le canal</div>
              <div className="px-1 text-[13px] text-white font-medium whitespace-pre-wrap break-words">{sondage.question}</div>
              <div className="px-1 text-[10px] text-slate-500">{sondage.quiz ? 'Quiz anonyme' : 'Sondage anonyme'}{sondage.multiple ? ' · choix multiples' : ''}</div>
              {sondage.options.map((o, i) => (o.trim() ? (
                <div key={i} className="mt-1.5 mx-1 flex items-center gap-2 text-[12px] text-white">
                  <span className={`w-3.5 h-3.5 shrink-0 border border-slate-500 ${sondage.multiple ? 'rounded' : 'rounded-full'}`} /><span className="break-words min-w-0">{o}</span>
                </div>) : null))}
              {boutonTexte.trim() && <div className="mt-2 rounded-lg bg-white/10 text-center text-[12px] text-white py-1.5 flex items-center justify-center gap-1"><Link2 size={11} /> {boutonTexte}</div>}
            </div>
          )}
          {mode === 'vocal' && (srcVocal || legende || boutonTexte.trim()) && (
            <div className="rounded-xl bg-[#0E1621] border border-white/10 p-2 max-w-[320px]" data-testid="canal-post-apercu">
              <div className="text-[10px] text-slate-500 px-1 pb-1">Aperçu dans le canal</div>
              <div className="flex items-center gap-2 px-1">
                <span className="w-8 h-8 rounded-full bg-sky-500/80 flex items-center justify-center text-white shrink-0"><Play size={14} fill="currentColor" /></span>
                <span className="flex-1 h-5 flex items-end gap-[2px]" aria-hidden="true">{[3, 6, 9, 5, 12, 8, 4, 10, 7, 13, 6, 9, 4, 8, 11, 5, 7, 3].map((h, i) => <span key={i} className="w-[3px] rounded-sm bg-sky-300/70" style={{ height: `${h + 4}px` }} />)}</span>
                <span className="text-[11px] text-slate-300 tabular-nums">{dureeVocal ? dureeTexte(dureeVocal) : '—'}</span>
              </div>
              {legende && <div className="text-[12px] text-white whitespace-pre-wrap px-1 pt-1.5">{legende}</div>}
              {boutonTexte.trim() && <div className="mt-1.5 rounded-lg bg-white/10 text-center text-[12px] text-white py-1.5 flex items-center justify-center gap-1"><Link2 size={11} /> {boutonTexte}</div>}
            </div>
          )}
          {mode === 'media' && (apercuSrc || legende || boutonTexte) && (
            <div className="rounded-xl bg-[#0E1621] border border-white/10 p-2 max-w-[320px]" data-testid="canal-post-apercu">
              <div className="text-[10px] text-slate-500 px-1 pb-1">Aperçu dans le canal</div>
              {apercuSrc && !apercuVideo && <img src={apercuSrc} alt="" className="w-full rounded-lg max-h-48 object-cover" />}
              {apercuSrc && apercuVideo && <div className="w-full h-24 rounded-lg bg-black/50 flex items-center justify-center"><Film size={20} className="text-slate-400" /></div>}
              {legende && <div className="text-[12px] text-white whitespace-pre-wrap px-1 pt-1.5">{legende}</div>}
              {boutonTexte.trim() && <div className="mt-1.5 rounded-lg bg-white/10 text-center text-[12px] text-white py-1.5 flex items-center justify-center gap-1"><Link2 size={11} /> {boutonTexte}</div>}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/5 text-sm font-semibold text-white">File des posts</div>
          {rows.length === 0 && <div className="px-5 py-12 text-center text-sm text-slate-500">Aucun post programmé.</div>}
          <div className="divide-y divide-white/5 max-h-[620px] overflow-y-auto">
            {rows.map(r => {
              const [cls, label] = STATUS[r.status] || STATUS.pending;
              return (
                <div key={r.id} className={`flex items-start gap-3 px-4 py-3 ${edition && edition.id === r.id ? 'bg-indigo-500/10' : ''}`} data-testid={`canal-post-row-${r.id}`}>
                  <Vignette key={`${r.id}-${r.media_url || ''}-${r.vignette_url || ''}`} r={r} onOuvrir={() => setGrand(r)} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] text-white truncate">
                      {r.type === 'sondage' ? ((r.sondage || {}).question || 'Sondage') : (r.legende || <span className="text-slate-500 italic">{r.type === 'vocal' ? 'vocal sans légende' : 'sans légende'}</span>)}
                    </div>
                    {r.type === 'sondage' && (
                      <div className="text-[11px] text-slate-400 truncate" data-testid={`canal-post-sondage-${r.id}`}>📊 {resumeSondage(r.sondage)} — {((r.sondage || {}).options || []).join(' / ')}</div>
                    )}
                    {r.type === 'vocal' && (
                      <div className="text-[11px] text-slate-400" data-testid={`canal-post-vocal-${r.id}`}>🎤 Vocal · {dureeTexte(r.duree_s)}{r.media_url ? '' : ' · fichier plus sur le serveur'}</div>
                    )}
                    {r.type === 'vocal' && r.media_url && (
                      <audio src={srcMedia(r.media_url, BACKEND)} controls preload="none" data-testid={`canal-post-audio-${r.id}`} className="mt-1 w-full max-w-[240px] h-8" />
                    )}
                    <div className="text-[11px] text-slate-500 truncate">📢 {r.chat_title}</div>
                    <div className="text-[11px] text-slate-300 whitespace-nowrap" data-testid={`canal-post-quand-${r.id}`}><Clock size={10} className="inline mr-1" />{fmtDateTime(r.scheduled_at)}</div>
                    {r.bouton_texte && <div className="text-[11px] text-slate-400 truncate">🔘 {r.bouton_texte} → <span className="text-slate-500">{r.bouton_url}</span></div>}
                    {r.error && <div className="text-[11px] text-rose-400/90">{r.error}</div>}
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${cls}`}>{label}</span>
                    {r.status === 'pending' && (
                      <div className="flex gap-0.5">
                        <button data-testid={`canal-post-modifier-${r.id}`} onClick={() => modifier(r)} title={r.type === 'sondage' ? 'Modifier (question, options, date, bouton)' : r.type === 'vocal' ? 'Modifier (audio, légende, date, bouton)' : 'Modifier (date, légende, bouton, photo/vidéo)'} className="text-slate-400 hover:text-indigo-300 p-1"><Pencil size={14} /></button>
                        <button data-testid={`canal-post-maintenant-${r.id}`} onClick={() => maintenant(r.id)} title="Publier maintenant" className="text-slate-400 hover:text-emerald-300 p-1"><Send size={14} /></button>
                        <button data-testid={`canal-post-annuler-${r.id}`} onClick={() => annuler(r.id)} title="Annuler" className="text-slate-400 hover:text-rose-400 p-1"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <ApercuGrand post={grand} onFermer={() => setGrand(null)} />
    </div>
  );
}
